(() => {
  const grid = document.getElementById('ProductGridContainer');
  if (!grid) return;

  const selector = '.rwady-collection-product-card[x-ignore]';
  const cardSelector = '.rwady-collection-product-card';
  const desktopDescriptions = window.matchMedia('(min-width: 768px)');
  const pending = new Set();
  const observed = new WeakSet();
  let intersectionObserver;
  let alpineReady = false;

  function activate(card) {
    if (!card.isConnected || !card.hasAttribute('x-ignore')) return;
    intersectionObserver?.unobserve(card);
    pending.delete(card);

    if (!alpineReady) {
      // Alpine has not traversed the grid yet, so it will initialize this card.
      card.removeAttribute('x-ignore');
      return;
    }

    const alpine = window.Alpine;
    if (!alpine?.initTree || !alpine?.mutateDom) return;
    const hasCardScope = Boolean(card._x_dataStack);
    alpine.mutateDom(() => {
      card.removeAttribute('x-ignore');
      // Alpine 3.10's x-ignore sets this flag during the first traversal.
      delete card._x_ignore;
      // A root x-data card was initialized once by Alpine even when x-ignore
      // prevented its children. Reinitialize only those children in that case.
      if (hasCardScope) Array.from(card.children).forEach(child => alpine.initTree(child));
      else alpine.initTree(card);
    });
  }

  function hydrateDescriptions(card = grid) {
    if (!desktopDescriptions.matches) return;
    card.querySelectorAll('template[data-rwady-card-description]').forEach(template => {
      template.replaceWith(template.content);
    });
  }

  const imageCards = new WeakMap();
  const imageObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { entry.target.dataset.imageVisible = String(entry.isIntersecting); });
  }) : null;
  function trackImage(card) {
    const compact = document.documentElement.classList.contains('rwady-compact-browse');
    const image = card.querySelector(compact ? '.rwady-compact-card img' : 'img.product-card__image');
    const previous = imageCards.get(card);
    if (previous?.image === image) return;
    previous?.cleanup();
    delete card.dataset.imagePending;
    delete card.dataset.imageVisible;
    if (!image) { imageCards.delete(card); return; }
    imageCards.set(card, { image, cleanup: () => {} });
    if (image.complete) return;
    card.dataset.imagePending = 'true';
    card.dataset.imageVisible = String(!imageObserver);
    const settle = () => {
      delete card.dataset.imagePending;
      delete card.dataset.imageVisible;
      imageObserver?.unobserve(card);
      image.removeEventListener('load', settle);
      image.removeEventListener('error', settle);
    };
    imageCards.set(card, { image, cleanup: settle });
    image.addEventListener('load', settle);
    image.addEventListener('error', settle);
    imageObserver?.observe(card);
    if (image.complete) settle();
  }
  const syncVisibility = () => {
    grid.dataset.pageHidden = String(document.hidden);
  };
  document.addEventListener('visibilitychange', syncVisibility);
  syncVisibility();

  function scan() {
    const cards = grid.querySelectorAll(cardSelector);
    cards.forEach(trackImage);
    if (!('IntersectionObserver' in window)) {
      cards.forEach(card => {
        hydrateDescriptions(card);
        activate(card);
      });
      return;
    }

    if (!intersectionObserver) {
      intersectionObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          hydrateDescriptions(entry.target);
          if (alpineReady) activate(entry.target);
          else if (entry.target.hasAttribute('x-ignore')) pending.add(entry.target);
        });
      }, { rootMargin: '200px 0px' });
    }

    cards.forEach(card => {
      if (observed.has(card)) return;
      observed.add(card);
      intersectionObserver.observe(card);
    });
  }

  // The grid container stays mounted when filters or the 100-item view replace its contents.
  new MutationObserver(records => {
    // Ignore changes inside existing cards, including description hydration.
    const hasNewCards = records.some(record => Array.from(record.addedNodes).some(node =>
      node.nodeType === 1 && (node.matches?.(cardSelector) || node.querySelector?.(cardSelector))));
    if (hasNewCards) scan();
  }).observe(grid, { childList: true, subtree: true });

  // Activate before a tap or keyboard action even if an observer callback is late.
  for (const eventName of ['pointerdown', 'focusin']) {
    grid.addEventListener(eventName, event => {
      const card = event.target.closest?.(selector);
      if (card) activate(card);
    }, true);
  }

  document.addEventListener('alpine:initialized', () => {
    alpineReady = true;
    for (const card of pending) activate(card);
    scan();
  }, { once: true });

  // A viewport change is rare; hydrate remaining descriptions if desktop list view becomes available.
  if (desktopDescriptions.addEventListener) desktopDescriptions.addEventListener('change', () => hydrateDescriptions());
  else if (desktopDescriptions.addListener) desktopDescriptions.addListener(hydrateDescriptions);
  document.addEventListener('rwady:browse-mode-changed', scan);
  scan();
})();
